import React, { useCallback, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Pagination } from '@/components/ui/Pagination';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  useDeleteScenarioMutation,
  useGetScenarioUiSettingsQuery,
  useListCategoriesQuery,
  useListScenariosQuery,
  useSetScenarioApprovalMutation,
} from '@/store/apiSlice';
import { resolveMediaUrl } from '@/lib/resolveMediaUrl';
import { stripHtml } from '@/lib/utils';
import { Trash2 } from 'lucide-react';
import { toast } from 'sonner';

export interface ScenarioItem {
  id: string;
  name: string;
  description: string;
  severity?: string;
  severity_name?: string;
  category_name: string;
  type_name?: string;
  thumbnail_url: string;
  points?: number;
  type?: string;
  is_prepared?: boolean;
  is_approved?: boolean;
  /** Whether Tabletop Exercise (TTX) is enabled for this scenario. */
  ttx_enabled?: boolean;
}

function getApiErrorMessage(error: unknown, fallback: string): string {
  if (error && typeof error === 'object' && 'data' in error) {
    const data = (error as { data?: unknown }).data;
    if (data && typeof data === 'object' && data !== null && 'detail' in data) {
      const detail = (data as { detail?: unknown }).detail;
      if (typeof detail === 'string' && detail.trim()) return detail;
    }
  }
  return fallback;
}

function getDifficultyColor(difficulty: string) {
  switch (difficulty) {
    case 'Easy':
      return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
    case 'Medium':
      return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400';
    case 'Hard':
      return 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400';
    case 'Expert':
      return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400';
    default:
      return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300';
  }
}

export function ScenarioList() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState('');
  const [unapprovingId, setUnapprovingId] = useState<string | null>(null);
  const [approvingId, setApprovingId] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ScenarioItem | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const typeFilter = 'all';

  const page = searchParams.get('page');
  const approved = searchParams.get('approved');
  const ttxEnabled = searchParams.get('ttx_enabled');
  const categoryId = searchParams.get('category') ?? '';
  const { data: categoryResponse } = useListCategoriesQuery({ page: 1, page_size: 100 });
  const categories = categoryResponse?.results ?? [];
  const isApprovedFilter =
    approved === 'true' || approved === '1'
      ? true
      : approved === 'false' || approved === '0'
        ? false
        : undefined;
  const ttxEnabledFilter =
    ttxEnabled === 'true' || ttxEnabled === '1'
      ? true
      : ttxEnabled === 'false' || ttxEnabled === '0'
        ? false
        : undefined;
  const {
    data: listResponse,
    isLoading,
    isFetching,
  } = useListScenariosQuery({
    q: searchTerm || undefined,
    category: categoryId || undefined,
    is_approved: isApprovedFilter,
    ttx_enabled: ttxEnabledFilter,
    page: page ?? undefined,
  });
  const [setScenarioApproval] = useSetScenarioApprovalMutation();
  const [deleteScenario] = useDeleteScenarioMutation();
  const { data: uiSettings } = useGetScenarioUiSettingsQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });
  const showDeleteButton = Boolean(uiSettings?.show_scenario_delete_button);

  const createQueryString = useCallback(
    (name: string, value: string) => {
      const params = new URLSearchParams(searchParams.toString());
      params.set(name, value);
      return params.toString();
    },
    [searchParams]
  );

  const data = (listResponse?.results ?? []) as ScenarioItem[];
  const currentPage = listResponse?.current_page ?? 1;
  const nextPage = listResponse?.next ?? null;
  const prevPage = listResponse?.previous ?? null;
  const totalPages = listResponse?.total_pages ?? 1;

  const handleUnapprove = async (scenarioId: string) => {
    setUnapprovingId(scenarioId);
    try {
      await setScenarioApproval({ id: scenarioId, is_approved: false }).unwrap();
    } catch (err) {
      console.error('Failed to unapprove scenario:', err);
    } finally {
      setUnapprovingId(null);
    }
  };

  const handleApprove = async (scenarioId: string) => {
    setApprovingId(scenarioId);
    try {
      await setScenarioApproval({ id: scenarioId, is_approved: true }).unwrap();
    } catch (err) {
      console.error('Failed to approve scenario:', err);
    } finally {
      setApprovingId(null);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    const scenarioId = deleteTarget.id;
    const scenarioName = deleteTarget.name;
    setDeletingId(scenarioId);
    try {
      await deleteScenario(scenarioId).unwrap();
      toast.success(`Deleted "${scenarioName}".`);
      setDeleteTarget(null);
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Could not delete this scenario.'));
    } finally {
      setDeletingId(null);
    }
  };

  const pathname = '/scenarios';

  const filteredData = data.filter((scenario) => {
    const matchesSearch =
      scenario.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (scenario.category_name || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = typeFilter === 'all' || scenario.type === typeFilter;
    return matchesSearch && matchesType;
  });

  const handleCategoryChange = (value: string) => {
    const params = new URLSearchParams(searchParams.toString());
    if (value) params.set('category', value);
    else params.delete('category');
    params.delete('page');
    const next = params.toString();
    navigate(next ? `${pathname}?${next}` : pathname);
  };

  const handlePageClick = (url: string | null) => {
    if (!url) return;
    try {
      const parsedUrl = new URL(url);
      const pageParam = parsedUrl.searchParams.get('page');
      if (pageParam) {
        navigate(`?${createQueryString('page', pageParam)}`);
      }
    } catch {
      // ignore
    }
  };

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <div className="md:col-span-2">
          <Input
            placeholder="Search scenarios..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full"
          />
        </div>
        <div className="md:col-span-2">
          <Select
            aria-label="Filter by category"
            value={categoryId}
            onChange={(e) => handleCategoryChange(e.target.value)}
          >
            <option value="">All categories</option>
            {categories.map((category) => (
              <option key={category.id} value={String(category.id)}>
                {category.name}
              </option>
            ))}
          </Select>
        </div>
      </div>
      {isLoading || isFetching ? (
        <div className="flex justify-center items-center py-12">
          <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <div className="flex flex-col gap-5">
          {filteredData.length > 0 ? (
            <>
              <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {filteredData.map((scenario) => (
                  <Card
                    key={scenario.id}
                    className="group overflow-hidden transition-all duration-300 hover:shadow-lg hover:-translate-y-1"
                  >
                    <div className="relative h-40 bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-800 dark:to-gray-900">
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent z-10" />
                      <img
                        src={resolveMediaUrl(scenario.thumbnail_url) || scenario.thumbnail_url}
                        alt={scenario.name}
                        className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity duration-300"
                      />
                      <div className="absolute bottom-0 left-0 p-4 z-20 flex flex-wrap gap-2">
                        <Badge className={`${getDifficultyColor(scenario.severity_name || scenario.severity || '')} font-medium`}>
                          {scenario.severity_name || scenario.severity || 'N/A'}
                        </Badge>
                        {scenario.type_name && (
                          <Badge variant="secondary">{scenario.type_name}</Badge>
                        )}
                        {typeof scenario.ttx_enabled === 'boolean' && (
                          <Badge
                            className={
                              scenario.ttx_enabled
                                ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300 font-medium'
                                : 'bg-muted/80 text-muted-foreground font-normal'
                            }
                          >
                            TTX {scenario.ttx_enabled ? 'On' : 'Off'}
                          </Badge>
                        )}
                        {typeof scenario.is_prepared === 'boolean' && (
                          <Badge
                            className={
                              scenario.is_prepared
                                ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300 font-medium'
                                : 'bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-300 font-medium'
                            }
                          >
                            {scenario.is_prepared ? 'Prepared' : 'Not Prepared'}
                          </Badge>
                        )}
                      </div>
                    </div>
                    <CardHeader>
                      <CardTitle className="text-xl line-clamp-1">{scenario.name}</CardTitle>
                      <CardDescription className="line-clamp-2 h-12">
                        {stripHtml(scenario.description)}
                      </CardDescription>
                      <div className="flex flex-wrap gap-2 mt-2">
                        <Badge variant="outline" className="text-xs">
                          {scenario.category_name}
                        </Badge>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-center justify-between text-sm">
                        {/* <div className="flex items-center gap-1">
                          <span className="text-yellow-500">★</span>
                          <span className="font-medium">4.5</span>
                          <span className="text-muted-foreground text-xs">(93)</span>
                        </div>
                        <div className="text-muted-foreground flex items-center gap-2">
                          <Trophy className="h-4 w-4 text-warning" />
                          <span className="font-medium">{scenario.points ?? 0} points</span>
                        </div> */}
                      </div>
                      {typeof scenario.ttx_enabled === 'boolean' && (
                        <div className="mt-2 text-xs text-muted-foreground">
                          TTX:{' '}
                          <span
                            className={
                              scenario.ttx_enabled
                                ? 'text-amber-600 dark:text-amber-400 font-medium'
                                : ''
                            }
                          >
                            {scenario.ttx_enabled ? 'Enabled' : 'Disabled'}
                          </span>
                        </div>
                      )}
                      {typeof scenario.is_prepared === 'boolean' && (
                        <div className="mt-1 text-xs text-muted-foreground">
                          Approval prerequisite:{' '}
                          <span
                            className={
                              scenario.is_prepared
                                ? 'text-emerald-600 dark:text-emerald-400 font-medium'
                                : 'text-rose-600 dark:text-rose-400 font-medium'
                            }
                          >
                            {scenario.is_prepared ? 'Prepared' : 'Prepare first'}
                          </span>
                        </div>
                      )}
                    </CardContent>
                    <CardFooter className="flex flex-col gap-2 border-t pt-4">
                      <Button
                        size="sm"
                        className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                        asChild
                      >
                        <Link to={`/scenarios/${scenario.id}/detail`}>View Details</Link>
                      </Button>
                      <div className="flex w-full items-center gap-2">
                        <Button size="sm" variant="outline" className="flex-1" asChild>
                          <Link to={`/scenarios/${scenario.id}/edit`}>Edit</Link>
                        </Button>
                        {scenario.is_prepared &&
                          (scenario.is_approved ? (
                            <Button
                              size="sm"
                              variant="outline"
                              className="flex-1"
                              onClick={() => handleUnapprove(scenario.id)}
                              disabled={unapprovingId === scenario.id}
                            >
                              {unapprovingId === scenario.id ? 'Unapproving...' : 'Unapprove'}
                            </Button>
                          ) : (
                            <Button
                              size="sm"
                              variant="outline"
                              className="flex-1 border-emerald-600/50 text-emerald-700 hover:bg-emerald-50 hover:text-emerald-800 dark:text-emerald-400 dark:hover:bg-emerald-950/40 dark:hover:text-emerald-300"
                              onClick={() => handleApprove(scenario.id)}
                              disabled={approvingId === scenario.id}
                            >
                              {approvingId === scenario.id ? 'Approving...' : 'Approve'}
                            </Button>
                          ))}
                        {showDeleteButton && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="shrink-0 px-2.5 border-destructive/50 text-destructive hover:border-destructive hover:bg-destructive/10 hover:text-destructive"
                            aria-label={`Delete ${scenario.name}`}
                            title="Delete scenario"
                            onClick={() => setDeleteTarget(scenario)}
                            disabled={deletingId === scenario.id}
                          >
                            <Trash2 />
                            <span className="sr-only">
                              {deletingId === scenario.id ? 'Deleting' : 'Delete'}
                            </span>
                          </Button>
                        )}
                      </div>
                    </CardFooter>
                  </Card>
                ))}
              </div>
              <Pagination
                totalPages={totalPages}
                currentPage={currentPage}
                onNext={() => handlePageClick(nextPage)}
                onPrev={() => handlePageClick(prevPage)}
                onFirst={() => navigate(`${pathname}?${createQueryString('page', '1')}`)}
                onLast={() => navigate(`${pathname}?${createQueryString('page', String(totalPages))}`)}
              />
            </>
          ) : (
            <div className="col-span-full text-center py-12">
              <div className="text-4xl mb-2">🔍</div>
              <h3 className="text-lg font-medium">No scenarios found</h3>
              <p className="text-muted-foreground">Try adjusting your search or filters</p>
            </div>
          )}
        </div>
      )}
      <AlertDialog
        open={!!deleteTarget}
        onOpenChange={(open) => {
          if (!open && !deletingId) setDeleteTarget(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this scenario?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget
                ? `"${deleteTarget.name}" will be removed, including its team setup, walkthrough files, and infrastructure blueprint. Flags and injects used only by this scenario are removed. Draft runs that have not started are removed. Past reports are kept. A scenario cannot be deleted while a run is in progress or still launching.`
                : 'This scenario will be removed.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel type="button" disabled={!!deletingId}>
              Cancel
            </AlertDialogCancel>
            <Button
              type="button"
              variant="destructive"
              onClick={handleDelete}
              disabled={!!deletingId}
            >
              {deletingId ? 'Deleting...' : 'Delete scenario'}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
